import React, { useState } from "react";
import {
  useReactTable,
  flexRender,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
} from "@tanstack/table-core";
import Icon from "../../libraries/icons";
import Loader from "./Loader";

function TanStackTable({
  data = [],
  columns = [],
  loading = false,
  pageCount = 1,
  pageIndex = 0,
  pageSize = 10,
  onPaginationChange,
  onRowSelectionChange,
  selectedRowIds = {},
  extraTableFunctions = [],
  noDataComponent = "No se encontraron registros",
  getDataFunction,
  screenWidth = 1024,
  multipleFunction,
  multipleFunctionLabel,
  multipleFunctionIcon,
  conditionalRowStyles,
  customLoaderComponent,
  onRowClicked,
  selectableRowDisabled,
  query,
  pagination = true,
  enableRowSelection = false,
  sorting = [],
  onSortingChange,
  expandableRows = false,
  expandableRowsComponent,
  onRowExpand,
}) {  // Estado local para filas expandidas y sorting
  const [expanded, setExpanded] = useState({});
  const [localSorting, setLocalSorting] = useState([]);
  const [internalSelectedRowIds, setInternalSelectedRowIds] = useState({});
  
  const actualSelectedRowIds = selectedRowIds && Object.keys(selectedRowIds).length > 0 ? selectedRowIds : internalSelectedRowIds;
  

  const handleRowSelectionChange = (updaterOrValue) => {
    
    if (onRowSelectionChange) {
      onRowSelectionChange(updaterOrValue);
    } else {
      setInternalSelectedRowIds(prevSelection => {
        const newSelection = typeof updaterOrValue === 'function' ? 
          updaterOrValue(prevSelection) : updaterOrValue;
        return newSelection;
      });
    }
  };
  const handleSortingChange = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(localSorting) : updaterOrValue;
    setLocalSorting(newSorting);
    
    if (onSortingChange) {
      if (!newSorting || newSorting.length === 0) {
        onSortingChange([]); 
      } else {
        onSortingChange(newSorting);
      }
    }
  };
  const handleRowClick = (row) => {
    if (onRowClicked) {
      onRowClicked(row.original);
    }
    if (expandableRows && onRowExpand) {
      onRowExpand(row.original);
      setExpanded(prev => ({
        ...prev,
        [row.id]: !prev[row.id]
      }));
    }
  };


  const table = useReactTable({
    data,
    columns,    state: {
      pagination: { pageIndex, pageSize },
      rowSelection: actualSelectedRowIds,
      sorting: localSorting,
      expanded,
    },    pageCount,
    manualPagination: true,
    manualSorting: true, 
    enableSorting: true, 
    enableSortingRemoval: true, 
    enableExpanding: expandableRows,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    getRowId: (row, index) => {
      // Priorizar ID del objeto, luego usar índice
      const id = row.id?.toString() || `row-${index}`;
      return id;
    },
  });


  // Calcula el número de columnas
  const colCount = columns.length + (enableRowSelection ? 1 : 0);  
  const [enableButtons, setEnableButtons] = useState(false);

  return (
    <div className={`${data?.length ? "rounded-t-box" : "rounded-box"} grid relative`}>
      {loading && (
        <div className="flex items-center justify-center min-h-[248px]">
          {customLoaderComponent || <Loader spinnerClassName="text-primary h-12 w-12" />}
        </div>
      )}
      {!loading && (
        <table className="min-w-full border bg-white shadow-md rounded-md">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {enableRowSelection && (
                  <th className="p-2 align-middle w-10 text-center">
                    <input
                      type="checkbox"
                      checked={table.getIsAllRowsSelected()}
                      ref={(el) => {
                        if (el) el.indeterminate = table.getIsSomeRowsSelected();
                      }}
                      onChange={(e) => {
                        table.getToggleAllRowsSelectedHandler()(e);
                      }}
                    />
                  </th>
                )}                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`p-2 text-left font-bold align-middle ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''} ${header.column.columnDef.meta?.className || ''}`}
                    style={header.column.columnDef.meta?.style || {}}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && header.column.getIsSorted() === "asc" && <span> ▲</span>}
                    {header.column.getCanSort() && header.column.getIsSorted() === "desc" && <span> ▼</span>}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="text-center py-10">
                  {noDataComponent}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className={`border-t cursor-pointer ${conditionalRowStyles ? conditionalRowStyles(row.original) : ""}`}
                    onClick={(e) => {
                      if (e.target.type === 'checkbox') return;
                      handleRowClick(row);
                    }}
                  >
                    {enableRowSelection && (
                      <td className="p-2 align-middle w-10 text-center py-3">
                        <input
                          type="checkbox"
                          checked={row.getIsSelected()}
                          onChange={(e) => {
                            e.stopPropagation();
                            row.getToggleSelectedHandler()(e);
                          }}
                          disabled={selectableRowDisabled ? selectableRowDisabled(row.original) : false}
                        />
                      </td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`pl-2 align-middle py-3 text-sm ${cell.column.columnDef.meta?.className || ''}`}
                        style={cell.column.columnDef.meta?.style || {}}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandableRows && expanded[row.id] && expandableRowsComponent && (
                    <tr>
                      <td colSpan={colCount} className="p-4 bg-gray-50">
                        {expandableRowsComponent({ data: row.original })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={colCount}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-2 mx-2">
                  {(multipleFunction || (extraTableFunctions && extraTableFunctions.length > 0)) && (
                    <div className="flex flex-row flex-wrap gap-2">
                      {multipleFunction && (
                        <button
                          onClick={() => {
                            const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
                            multipleFunction(selectedRows);
                          }}
                          disabled={enableButtons || table.getSelectedRowModel().rows.length === 0}
                          className="btn btn-xs bg-base-150 border-0"
                        >
                          <Icon className="h-4 w-4 mr-2" name={multipleFunctionIcon || "trash"} />
                          {multipleFunctionLabel || "Acción múltiple"}
                        </button>
                      )}
                      {extraTableFunctions.map((func, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
                            func.customFunction(selectedRows);
                          }}
                          disabled={enableButtons || table.getSelectedRowModel().rows.length === 0}
                          className="btn btn-xs bg-base-150 border-0"
                        >
                          <Icon className="h-4 w-4 mr-2" name={func.icon} />
                          {func.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {pagination && (
                    <div className={`flex items-center gap-2 ${
                      !(multipleFunction || (extraTableFunctions && extraTableFunctions.length > 0)) 
                        ? 'md:mx-auto' 
                        : ''
                    }`}>
                      <button
                        className="btn btn-xs"
                        onClick={() => {
                          const currentPage = table.getState().pagination.pageIndex;
                          const newPagination = { pageIndex: currentPage - 1, pageSize };
                          if (onPaginationChange) onPaginationChange(newPagination);
                        }}
                        disabled={!table.getCanPreviousPage()}
                      >
                        <Icon className="h-4 w-4" name="cheveron_left" />
                      </button>
                      <span>
                        Página{" "}
                        <strong>
                          {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                        </strong>
                      </span>
                      <button
                        className="btn btn-xs"
                        onClick={() => {
                          const currentPage = table.getState().pagination.pageIndex;
                          const newPagination = { pageIndex: currentPage + 1, pageSize };
                          if (onPaginationChange) onPaginationChange(newPagination);
                        }}
                        disabled={!table.getCanNextPage()}
                      >
                        <Icon className="h-4 w-4" name="cheveron_right" />
                      </button>
                    </div>
                  )}
                  
                  {pagination && query && query.records > 0 && (
                    <div className="text-sm leading-5 text-gray-700 hidden sm:block">
                      <span className="font-medium p-1">{(query.startIndex ?? 0) + 1}</span>
                      a
                      <span className="font-medium p-1">{(query.endIndex ?? 0) + 1}</span>
                      de
                      <span className="font-medium p-1">{query.records}</span>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

export default TanStackTable;